<template>
  <div>
    <div v-if="!loading && !isRevenueStreamsEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">

          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />

          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isRevenueStreamsEmpty"
      :disabled="!canEdit || disabled"
      :title="$tc('addRevenueStream')"
      @click="onShowAddNewDialog"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addRevenueStream')"
            horizontal
            min-height="62px"
            @click="onShowAddNewDialog"
          />
        </el-col>
        <el-col
          v-for="revenueStream in filterRevenueStreams"
          :key="revenueStream.id"
          class="d-flex mb-2"
          :md="12"
        >
          <set-up-and-projections-card
            class="w-100"
            :entity="revenueStream"
            :name="revenueStream.name"
            :date="`${$moment(revenueStream.starts).format('MMM YYYY')} - ${$moment(revenueStream.ends).format('MMM YYYY')}`"
            :amount="getFormattedAmount(revenueStream.average)"
            :frequency="$t('pages.businessGuide.monthlyAverage')"
            :type="revenueStream.type"
            :disabled="!canEdit"
            view="list"
            @edit="onEditRevenueStreams"
            @copy="onCopyRevenueStreams"
            @delete="onDeleteRevenueStreams"
          />
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Revenue Stream -->
    <dialog-add-revenue-stream
      v-if="canEdit"
      :dialog-add-revenue-stream-visible="dialogAddNewVisible"
      :revenue-stream-action="revenueStreamAction"
      @close-dialog-add-revenue-streams="dialogAddNewVisible = false; revenueStreamAction = null"
      @close-dialog-on-button="dialogAddNewVisible = false"
      @on-update-revenue-streams-view="onUpdateAddNewDialog()"
    />
    <!-- /Dialog Add Revenue Stream -->

    <!-- Dialog Remove Revenue Stream -->
    <dialog-remove-revenue-stream
      :dialog-remove-revenue-stream-visible="dialogRemoveRevenueStreamVisible"
      :revenue-stream-action-to-delete="revenueStreamActionToDelete"
      @close-dialog-remove-revenue-stream="onCloseDialogRemoveRevenueStream"
      @on-update-revenue-streams-view="onUpdateRevenueStreamView"
    />
    <!-- /Dialog Remove Revenue Stream -->
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddRevenueStream from '@/views/Home/StoryMode/Forecast/RevenueStreams/Dialogs/DialogAddRevenueStream.vue'
import DialogRemoveRevenueStream from '@/views/Home/StoryMode/Forecast/RevenueStreams/Dialogs/DialogRemoveRevenueStream.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

import EventBus from '@/event-bus'

export default {
  name: 'RevenueStreamsList',

  components: {
    EraseIconButton,
    DialogRemoveRevenueStream,
    DialogAddRevenueStream,
    BusinessGuideSearch,
    SetUpAndProjectionsCard,
    AddNewCard
  },

  mixins: [
    MixinApiCalculations,
    MixinCurrencies,
    MixinDeleteIdeaExamples,
    MixinIdeaRoles
  ],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchValue: '',
      loading: false,
      revenueStreamAction: null,
      revenueStreamActionToDelete: null,
      dialogAddNewVisible: false,
      dialogRemoveRevenueStreamVisible: false
    }
  },

  computed: {
    ...mapState(['idea']),
    ...mapGetters('idea', {
      revenueStreams: 'getRevenueStreams'
    }),

    filterRevenueStreams () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.revenueStreams.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.revenueStreams
    },

    isRevenueStreamsEmpty () {
      return !this.revenueStreams.length
    },

    removeButtonVisible () {
      return this.canEdit && this.revenueHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onShowAddNewDialog()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdateAddNewDialog()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    onShowAddNewDialog () {
      this.getRevenueStreams()
      this.dialogAddNewVisible = true
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getRevenueStreams(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('revenue-streams')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    onEditRevenueStreams (revenueStream) {
      this.revenueStreamAction = Object.assign({}, revenueStream)
      this.dialogAddNewVisible = true
    },

    onCopyRevenueStreams (revenueStream) {
      this.revenueStreamAction = Object.assign({}, revenueStream)
      this.revenueStreamAction.id = 0
      this.dialogAddNewVisible = true
    },

    onDeleteRevenueStreams (revenueStream) {
      this.revenueStreamActionToDelete = Object.assign({}, revenueStream)
      this.dialogRemoveRevenueStreamVisible = true
    },

    onCloseDialogRemoveRevenueStream () {
      this.dialogRemoveRevenueStreamVisible = false
      this.revenueStreamActionToDelete = null
    },

    async onUpdateRevenueStreamView () {
      this.emitLoading(true)
      await this.getRevenueStreams()
      await this.getTotalCosts()
      await this.getProfitAndCashFlow()
      this.emitLoading(false)
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
